import { ChangeDetectorRef, Component } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { SeConstraintNotifierService } from '@se/constraint';
import { Logger } from '@se/se.ui.html5';
import moment, { Moment } from 'moment';
import { map, takeUntil } from 'rxjs/operators';
import { fromServerIsoString, SE_UI_DATE_FORMATS } from '@se/platform';

import { CpDateTimeControl } from '../../cp-configuration/view-model';
import { CpModelService } from '../../cp-model.service';

import { AbstractControlComponent } from '../abstract-control';

@Component({
    selector: 'cp-datetime',
    templateUrl: './datetime.component.html',
    styleUrls: ['./datetime.component.scss'],
    providers: [{ provide: MAT_DATE_FORMATS, useValue: SE_UI_DATE_FORMATS }],
})
export class CpDatetimeComponent extends AbstractControlComponent {
    control!: CpDateTimeControl;

    isControlInError = false;
    isModelInError = false;

    dateFormControl!: FormControl;
    timeFormControl!: FormControl;

    model: Moment | null = null;

    constructor(
        cdr: ChangeDetectorRef,
        constraintNotifierService: SeConstraintNotifierService,
        logger: Logger,
        private modelService: CpModelService,
    ) {
        super(cdr, constraintNotifierService, logger);
    }

    hasError() {
        return !!this.formControl?.errors || this.isControlInError || this.isModelInError;
    }

    onDateTimeChange(dateTime: Moment) {
        if (!dateTime) {
            this.valueChange(null);
        } else {
            this.valueChange(dateTime);
        }
    }

    onFocus() {
        this.focusin.emit();
    }

    protected init() {
        this.dataBind();
    }

    protected onModelChange(model: Moment) {
        return this.control.onModelChange(this.modelService, model, !this.isDisabled);
    }

    private dataBind() {
        if (!this.control.dataBinding) {
            return;
        }

        this.control
            .getEntityObservable(this.modelService)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(this._entitySubject);

        let initialValue = this.control.getInitialValue(this.modelService);
        if (moment.isMoment(initialValue)) {
            initialValue = initialValue.toISOString();
        }
        if (initialValue !== undefined && initialValue !== null && typeof initialValue !== 'string') {
            this.logger &&
                this.logger.error(
                    `Invalid initial value for ${this.control.name}: ${initialValue}. Component not initialized`,
                );
            return;
        }

        this.model = this.strToModel(initialValue as any);

        this.formControl = new FormControl(
            this.model,
            this.makeValidator(this._entitySubject, this.control.dataBinding.propertyName, this.control.required),
        );

        this.formControl.statusChanges
            .pipe(
                takeUntil(this.destroyed$),
                map((status) => status === 'VALID' || status === 'DISABLED'),
            )
            .subscribe((isValid) => {
                this.isModelInError = !isValid;
            });

        this.control
            .getModelObservable(this.modelService)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((model) => {
                this.valueChange(model);
                this.logger?.trace(`Model update ${this.control.name} ${model}`);
            });

        this.form.setControl(this.control.name, this.formControl);
    }

    private valueChange(model: Moment | string | null) {
        if (model === null) {
            if (this.formControl?.value === null) {
                return;
            }
            this.formControl?.setValue(null);
            return;
        }

        if (typeof model === 'string') {
            model = fromServerIsoString(model);
        }

        if (this.formControl?.value && model.isSame(this.formControl.value)) {
            return;
        }

        this.model = model;
        this.formControl?.setValue(model);
    }

    private strToModel(str: string) {
        if (!str) {
            return null;
        }

        const value = moment(str);

        if (value.isValid()) {
            return value.utc().add(value.utcOffset(), 'minute');
        } else {
            return null;
        }
    }
}
